import type { ApiResult } from "../http.js";
import type { HttpClient } from "../http.js";
import type { ProjectProgressInvoiceDto } from "../types/project-progress-invoices.js";

export interface CreatePositionLinePayload {
  tilbudslistePositionId: string;
  sectionCode: string;
  positionNumber: string;
  name: string;
  kontraktsum: number;
  previousProgress: number;
  newProgress: number;
  invoiceAmount: number;
  /**
   * The delprojekt whose tilbudsliste owns this position, when a hovedsag bills on its
   * behalf. Omit for a project billing its own work — the server reads a missing value as
   * the invoicing project.
   */
  sourceProjectId?: string | null;
}

export interface CreateAftaleseddelLinePayload {
  aftaleseddelId: string;
  number: number;
  caseNumber?: string | null;
  description: string;
  amount: number;
  sourceProjectId?: string | null;
}

export interface CreateProjectProgressInvoicePayload {
  positionLines: CreatePositionLinePayload[];
  aftaleseddelLines: CreateAftaleseddelLinePayload[];
}

export interface MarkProgressInvoicePaidPayload {
  amount: number;
  /** ISO 8601 */
  paidAt: string;
}

export class ProjectProgressInvoicesApi {
  constructor(private readonly http: HttpClient) {}

  create(
    projectId: string,
    payload: CreateProjectProgressInvoicePayload,
  ): Promise<ApiResult<string>> {
    return this.http.post<string>(
      `/api/projects/${encodeURIComponent(projectId)}/progress-invoices`,
      { body: payload },
    );
  }

  /**
   * @param scope `"tree"` returns every progress invoice in the project's
   * hovedsag/delprojekt hierarchy. The invoicing dialog needs it to see what a hovedsag has
   * already billed on a delprojekt's behalf — those invoices hang off an ancestor, so
   * this project's own list is not enough to stop a double-billing.
   */
  list(
    projectId: string,
    scope?: "tree",
  ): Promise<ApiResult<ProjectProgressInvoiceDto[]>> {
    const query = scope ? `?scope=${scope}` : "";
    return this.http.get<ProjectProgressInvoiceDto[]>(
      `/api/projects/${encodeURIComponent(projectId)}/progress-invoices${query}`,
    );
  }

  get(projectId: string, id: string): Promise<ApiResult<ProjectProgressInvoiceDto>> {
    return this.http.get<ProjectProgressInvoiceDto>(
      `/api/projects/${encodeURIComponent(projectId)}/progress-invoices/${encodeURIComponent(id)}`,
    );
  }

  markPaid(
    projectId: string,
    id: string,
    payload: MarkProgressInvoicePaidPayload,
  ): Promise<ApiResult<ProjectProgressInvoiceDto>> {
    return this.http.post<ProjectProgressInvoiceDto>(
      `/api/projects/${encodeURIComponent(projectId)}/progress-invoices/${encodeURIComponent(id)}/mark-paid`,
      { body: payload },
    );
  }
}
